import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface PshbSubscription {
  sub_id: string;
  channel_id: string;
  status: string;
  requested_at: string;
  verified_at: string | null;
  expires_at: string | null;
  last_notification_at: string | null;
  notifications_received: number;
  callback_url: string;
}

/**
 * Google PubSubHubbub subscriptions for real-time YouTube uploads.
 * Each connected channel auto-registers a 5-day lease with the PSHB hub
 * so new videos push signed notifications back to SociBaba within seconds.
 */
export const PshbSubscriptionsCard: React.FC = () => {
  const qc = useQueryClient();
  const subs = useQuery<{ items: PshbSubscription[] }>({
    queryKey: ["pshb-subscriptions"],
    queryFn: async () => (await api.get("/webhooks/youtube/pshb/subscriptions")).data,
  });
  const renew = useMutation({
    mutationFn: async () =>
      (await api.post("/webhooks/youtube/pshb/renew")).data,
    onSuccess: (d) => {
      toast.success(`Renewed ${d.renewed} subscription(s)`);
      qc.invalidateQueries({ queryKey: ["pshb-subscriptions"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Renew failed"),
  });

  return (
    <div className="sg-card p-6 mt-6" data-testid="pshb-subs-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="sg-label mb-1">Real-time YouTube webhooks</div>
          <div className="font-display text-lg font-semibold tracking-tight">
            PubSubHubbub subscriptions
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-2 max-w-2xl leading-relaxed">
            Each connected YouTube channel gets an auto-subscribe with Google&apos;s PSHB hub.
            When a new video is uploaded, the channel&apos;s Atom feed pushes SociBaba a
            signed notification and we immediately sync the video and its comments —
            typically within 1&ndash;2 seconds of upload. Leases expire in 5 days; renewal
            runs automatically at startup, plus you can trigger it here.
          </p>
        </div>
        <Button
          data-testid="pshb-renew-btn"
          variant="outline"
          size="sm"
          disabled={renew.isPending}
          onClick={() => renew.mutate()}
        >
          {renew.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
          Renew expiring
        </Button>
      </div>

      {subs.isLoading ? (
        <div className="sg-skeleton h-16" />
      ) : (subs.data?.items?.length ?? 0) === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)]" data-testid="no-pshb-subs">
          No subscriptions yet. Connect a YouTube channel and one will be auto-registered.
        </div>
      ) : (
        <div className="divide-y divide-[color:var(--sg-border)] border border-[color:var(--sg-border)] rounded-md">
          {subs.data!.items.map((s) => (
            <div key={s.sub_id} className="p-3 text-sm" data-testid={`pshb-sub-${s.channel_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs">{s.channel_id}</span>
                <span className={`sg-chip ${
                  s.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : s.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-rose-50 text-rose-700 border-rose-200"
                }`}>{s.status}</span>
                <span className="text-xs text-[color:var(--sg-muted)]">
                  {s.notifications_received} notification{s.notifications_received === 1 ? "" : "s"}
                </span>
              </div>
              <div className="text-xs text-[color:var(--sg-muted)] mt-1">
                {s.verified_at ? <>Verified {new Date(s.verified_at).toLocaleString()} · </> : null}
                {s.expires_at ? <>Expires {new Date(s.expires_at).toLocaleString()}</> : null}
                {s.last_notification_at ? <> · Last push {new Date(s.last_notification_at).toLocaleString()}</> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
